import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Lock, Check, Loader2, Search, Sparkles, Clock, AlertTriangle, History, Mail, UserRound, Eye, MessageSquare } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";

const ROMAN = { 1: "I", 2: "II", 3: "III", 4: "IV", 5: "V" };
const fmt = (iso) => (iso ? new Date(iso).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" }) : "");

function StageCard({ s, customer, onRelease, busy }) {
  const active = s.status === "active";
  return (
    <div data-testid={`vcb-stage-card-${s.n}`} className={`relative pl-12 pb-10 last:pb-0`}>
      <div className={`absolute left-0 top-0 h-full w-px ${active ? "bg-[#D8B23E]" : "bg-white/10"}`} />
      <div className={`absolute -left-[13px] top-0 h-7 w-7 rounded-full grid place-items-center border ${active ? "bg-[#D8B23E] border-[#D8B23E] text-black" : "bg-[#0A0906] border-[#D8B23E]/40 text-[#D8B23E]/70"}`}>
        {active ? <Check size={14} strokeWidth={3} /> : <Lock size={12} />}
      </div>
      <div className={`border p-6 ${active ? "border-[#D8B23E]/50 bg-[#0F0D08]" : "border-white/10 bg-black/40"}`}>
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <div className="font-grotesk text-[9px] uppercase tracking-[0.34em] text-[#8A6A1A]">Stage {s.numeral}</div>
            <h3 className="font-serif text-2xl md:text-3xl text-[#D8B23E] mt-1" data-testid={`vcb-stage-title-${s.n}`}>{s.title}</h3>
          </div>
          <span data-testid={`vcb-stage-status-${s.n}`} className={`font-grotesk text-[9px] uppercase tracking-[0.28em] px-3 py-1.5 border ${active ? "border-[#D8B23E] text-[#D8B23E]" : "border-white/15 text-[#8A8A82]"}`}>
            {active ? (s.is_current ? "Released · Active" : "Active") : "Locked"}
          </span>
        </div>
        <p className="font-serif text-[15px] leading-relaxed text-[#E8DFC4] mt-4">{s.narrative}</p>
        <ul className="mt-4 grid sm:grid-cols-2 gap-x-6 gap-y-1.5">
          {s.features.map((f) => <li key={f} className="font-body text-[12px] text-[#A9A9A2] flex gap-2"><span className="text-[#D8B23E]">◆</span>{f}</li>)}
        </ul>
        {active && (
          <div className="mt-5 flex flex-wrap gap-5 text-[11px] font-body text-[#8A8A82]" data-testid={`vcb-stage-meta-${s.n}`}>
            <span className="inline-flex items-center gap-1.5"><Clock size={11} className="text-[#D8B23E]" /> Released {fmt(s.released_at)}</span>
            <span>By <b className="text-[#C9C9C2]">{s.released_by}</b></span>
            {s.seen_at && <span>Seen by customer {fmt(s.seen_at)}</span>}
          </div>
        )}
        {active && s.note && <p className="mt-3 font-serif italic text-[13px] text-[#D8B23E] border-l border-[#D8B23E]/60 pl-3" data-testid={`vcb-stage-note-${s.n}`}>“{s.note}”</p>}
        {s.can_release && (
          <button data-testid={`vcb-release-stage-${s.n}`} onClick={() => onRelease(s)} disabled={busy}
                  className="mt-6 w-full bg-[#D8B23E] hover:bg-[#E5C450] text-black font-grotesk text-[12px] uppercase tracking-[0.34em] py-5 shadow-[0_18px_50px_-20px_rgba(216,178,62,0.9)] transition-all hover:-translate-y-0.5 disabled:opacity-50 inline-flex items-center justify-center gap-3">
            {busy ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />} {s.button}
          </button>
        )}
      </div>
    </div>
  );
}

const RM_EMPTY = { name: "", title: "", email: "", phone: "" };

function RMFields({ rm, setRm, prefix = "vcb-rm" }) {
  return (
    <div className="grid grid-cols-2 gap-2">
      {[["name", "Name *"], ["title", "Designation"], ["email", "E-mail"], ["phone", "Phone"]].map(([k, l]) => (
        <label key={k} className="block"><span className="block font-grotesk text-[8px] uppercase tracking-[0.24em] text-[#8A8A82] mb-1">{l}</span>
          <Input data-testid={`${prefix}-${k}`} value={rm[k]} onChange={(e) => setRm({ ...rm, [k]: e.target.value })} className="h-9 bg-black/40 border-white/15 text-sm" /></label>
      ))}
    </div>
  );
}

function RelationshipManagerAdmin({ api, customer, rm, onSaved }) {
  const [edit, setEdit] = useState(!rm?.name);
  const [form, setForm] = useState({ ...RM_EMPTY, ...(rm || {}) });
  const [busy, setBusy] = useState(false);
  const save = async () => {
    if (form.name.trim().length < 2) return toast.error("Relationship manager name is required");
    setBusy(true);
    try {
      const r = await api.put(`/admin/vcb/stages/${customer.id}/relationship-manager`, form);
      toast.success("Relationship manager assigned. Customer notified.");
      onSaved(r.data.relationship_manager); setEdit(false);
    } catch (e) { toast.error(e.response?.data?.detail || "Could not save"); } finally { setBusy(false); }
  };
  return (
    <div data-testid="vcb-admin-rm" className="mt-8 border border-[#D8B23E]/40 bg-[#0A0906] p-6">
      <div className="flex items-center justify-between gap-3">
        <div className="font-grotesk text-[9px] uppercase tracking-[0.34em] text-[#D8B23E] inline-flex items-center gap-2"><UserRound size={11} /> Dedicated relationship manager</div>
        {rm?.name && !edit && <button data-testid="vcb-admin-rm-edit" onClick={() => setEdit(true)} className="font-grotesk text-[9px] uppercase tracking-[0.24em] text-[#C9C9C2] hover:text-[#D8B23E]">Change</button>}
      </div>
      {edit ? (
        <div className="mt-4 space-y-3">
          <RMFields rm={form} setRm={setForm} prefix="vcb-admin-rm" />
          <div className="flex gap-2">
            <Button onClick={save} disabled={busy} data-testid="vcb-admin-rm-save" className="bg-[#D8B23E] text-black font-grotesk uppercase tracking-[0.24em] text-[10px] hover:bg-[#E5C450]">{busy ? <Loader2 size={12} className="animate-spin" /> : "Assign"}</Button>
            {rm?.name && <Button variant="outline" onClick={() => { setEdit(false); setForm({ ...RM_EMPTY, ...rm }); }} className="border-white/20 text-[#C9C9C2]">Cancel</Button>}
          </div>
        </div>
      ) : (
        <div className="mt-3">
          <div className="font-serif text-xl text-[#F5F5F0]" data-testid="vcb-admin-rm-name">{rm.name}</div>
          <div className="font-body text-[12px] text-[#A9A9A2]">{[rm.title, rm.email, rm.phone].filter(Boolean).join(" · ")}</div>
          <div className="font-body text-[11px] text-[#8A8A82] mt-2">Assigned {fmt(rm.assigned_at)} by {rm.assigned_by}</div>
        </div>
      )}
    </div>
  );
}

export function ReleaseStagesPanel({ api, customer, onReleased, autoRelease }) {
  const [data, setData] = useState(null);
  const [confirm, setConfirm] = useState(null);
  const [rm, setRm] = useState(RM_EMPTY);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const ref = useRef(null);
  const load = useCallback(() => api.get(`/admin/vcb/stages/${customer.id}`).then((r) => setData(r.data)).catch(() => toast.error("Could not load stages")), [api, customer.id]);
  useEffect(() => { load(); }, [load]);
  const autoDone = useRef(false);
  useEffect(() => {
    if (!data || !autoRelease || autoDone.current) return;
    autoDone.current = true;
    const s = data.stages.find((x) => x.n === Number(autoRelease));
    if (s?.can_release) setConfirm(s);
    ref.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  }, [data, autoRelease]);
  const release = async () => {
    if (confirm.n === 5 && rm.name.trim() && rm.name.trim().length < 2) return toast.error("Relationship manager name is too short");
    setBusy(true);
    try {
      const body = { stage: confirm.n, note: note.trim(), ...(confirm.n === 5 && rm.name.trim() ? { relationship_manager: rm } : {}) };
      const r = await api.post(`/admin/vcb/stages/${customer.id}/release`, body);
      setData((d) => ({ ...d, ...r.data }));
      toast.success(`Stage ${confirm.numeral} — ${confirm.title} released. Customer notified.`);
      setConfirm(null); setNote("");
      onReleased?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Release failed"); } finally { setBusy(false); }
  };
  if (!data) return <div className="py-10 text-center"><Loader2 className="animate-spin text-[#D8B23E] inline" /></div>;
  return (
    <section ref={ref} data-testid="vcb-release-stages-panel" className="border border-[#D8B23E]/30 bg-[#070604] p-6 md:p-8 scroll-mt-24">
      <div className="flex flex-wrap items-end justify-between gap-3 mb-8">
        <div>
          <div className="font-grotesk text-[9px] uppercase tracking-[0.34em] text-[#8A6A1A]">Release Stages</div>
          <h2 className="font-serif text-2xl md:text-3xl text-[#F5F5F0] mt-1">{customer.name || customer.email}</h2>
          <div className="font-body text-[12px] text-[#8A8A82] mt-1">{customer.email} · Current stage <b className="text-[#D8B23E]" data-testid="vcb-current-stage">{data.current_stage ? data.stages[data.current_stage - 1].numeral : "—"}</b> of V</div>
          <div className="font-body text-[11px] mt-1" data-testid="vcb-invitation-status">
            {data.invitation_email
              ? <span className="text-[#A9A082]">◆ VCB Invitation e-mail {data.invitation_email.delivery === "failed" ? <span className="text-[#E08A8A]">failed</span> : "sent"} {fmt(data.invitation_email.sent_at)} · ref <span className="text-[#D8B23E]">{data.invitation_email.reference}</span></span>
              : <span className="text-[#6A6A64]">◇ VCB Invitation e-mail not yet sent — fires automatically when this customer is added to VCB (before Stage I)</span>}
          </div>
        </div>
        {data.elite && <span className="font-grotesk text-[9px] uppercase tracking-[0.3em] border border-[#D8B23E] text-[#D8B23E] px-3 py-1.5" data-testid="vcb-elite-badge">◆ Vinci Elite</span>}
      </div>
      <div className="ml-3">{data.stages.map((s) => <StageCard key={s.n} s={s} customer={customer} onRelease={setConfirm} busy={busy} />)}</div>
      {data.current_stage >= 5 && <RelationshipManagerAdmin api={api} customer={customer} rm={data.relationship_manager} onSaved={(r) => setData((d) => ({ ...d, relationship_manager: r }))} />}
      {data.relationship_manager?.name && <RMThreadAdmin api={api} customer={customer} rm={data.relationship_manager} />}
      <Dialog open={!!confirm} onOpenChange={(o) => !o && !busy && setConfirm(null)}>
        <DialogContent className="bg-[#0A0906] border-[#D8B23E]/40 text-[#F5F5F0]" data-testid="vcb-release-confirm">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl text-[#D8B23E]">Confirm Release</DialogTitle>
            <DialogDescription className="font-body text-[14px] text-[#C9C9C2] pt-2 leading-relaxed">
              You are about to release Stage {confirm?.numeral} — {confirm?.title} for <b className="text-[#F5F5F0]">{customer.name || customer.email}</b>. This action cannot be undone. Confirm Release.
            </DialogDescription>
          </DialogHeader>
          {confirm?.n === 5 && (
            <div className="border border-[#D8B23E]/30 p-4" data-testid="vcb-release-rm-fields">
              <div className="font-grotesk text-[8px] uppercase tracking-[0.3em] text-[#D8B23E] mb-3">Dedicated relationship manager (optional — can be assigned later)</div>
              <RMFields rm={rm} setRm={setRm} prefix="vcb-release-rm" />
            </div>
          )}
          <div data-testid="vcb-release-note-wrap">
            <div className="flex justify-between font-grotesk text-[8px] uppercase tracking-[0.3em] text-[#D8B23E] mb-1.5"><span>Personal note to the customer (optional)</span><span className="text-[#8A8A82]">{note.length}/320</span></div>
            <Textarea data-testid="vcb-release-note" value={note} onChange={(e) => setNote(e.target.value.slice(0, 320))} rows={3} placeholder="Appears in the unlock e-mail and the customer's unlock cinematic…" className="bg-black/40 border-white/15 text-sm font-serif italic" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirm(null)} disabled={busy} className="border-white/20 text-[#C9C9C2]" data-testid="vcb-release-cancel">Cancel</Button>
            <Button onClick={release} disabled={busy} data-testid="vcb-release-confirm-btn" className="bg-[#D8B23E] text-black font-grotesk uppercase tracking-[0.24em] text-[11px] hover:bg-[#E5C450]">
              {busy ? <Loader2 size={13} className="animate-spin" /> : "Confirm Release"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </section>
  );
}

export function VCBStageDesk({ api }) {
  const [customers, setCustomers] = useState([]);
  const [q, setQ] = useState("");
  const [picked, setPicked] = useState(null);
  const [report, setReport] = useState(null);
  const [overdueDays, setOverdueDays] = useState(null);
  const [inboxTick, setInboxTick] = useState(0);
  useEffect(() => { const h = () => setInboxTick((t) => t + 1); window.addEventListener("vcb-rm-thread-changed", h); return () => window.removeEventListener("vcb-rm-thread-changed", h); }, []);
  const [searchParams, setSearchParams] = useSearchParams();
  const deepCustomer = searchParams.get("stage_customer");
  const deepRelease = searchParams.get("stage_release");
  const loadCustomers = useCallback(() => api.get("/admin/vcb/module/customers").then((r) => setCustomers(r.data?.customers || [])).catch(() => {}), [api]);
  useEffect(() => { loadCustomers(); }, [loadCustomers]);
  const loadReport = useCallback(() => api.get(`/admin/vcb/stages-report${overdueDays ? `?overdue_days=${Number(overdueDays)}` : ""}`).then((r) => { setReport(r.data); setOverdueDays((d) => d ?? r.data.overdue_days); }).catch(() => {}), [api, overdueDays]);
  useEffect(() => { loadReport(); }, [loadReport]);
  const saveTimer = useRef(null);
  const saveThreshold = useCallback((v) => {
    const n = Number(v);
    clearTimeout(saveTimer.current);
    if (!(n >= 1 && n <= 365)) return;
    saveTimer.current = setTimeout(() => api.put("/admin/vcb/stages/overdue-days", { overdue_days: n }).then(() => toast.success(`Digest threshold saved · ${n} days`)).catch(() => toast.error("Could not save threshold")), 700);
  }, [api]);
  useEffect(() => {
    if (!deepCustomer) return;
    api.get(`/admin/vcb/stages/${deepCustomer}`).then((r) => setPicked({ ...r.data.customer, autoRelease: deepRelease })).catch(() => toast.error("Customer from digest link not found"));
    setSearchParams((p) => { p.delete("stage_customer"); p.delete("stage_release"); return p; }, { replace: true });
  }, [api, deepCustomer, deepRelease, setSearchParams]);
  const list = useMemo(() => { const s = q.trim().toLowerCase(); const rank = (c) => (c.vcb_status === "inducted" || c.vcb_status === "active" ? 0 : 1); return customers.filter((c) => !s || c.email?.toLowerCase().includes(s) || c.name?.toLowerCase().includes(s)).sort((a, b) => rank(a) - rank(b)).slice(0, 8); }, [customers, q]);
  return (
    <div className="space-y-8" data-testid="vcb-stage-desk">
      <div className="border border-white/10 bg-black/40 p-6">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <div className="font-grotesk text-[9px] uppercase tracking-[0.34em] text-[#8A6A1A]">Stage-wise Feature Release</div>
            <h2 className="font-serif text-2xl text-[#F5F5F0] mt-1">Select a VCB customer profile</h2>
          </div>
          <InvitationPreview api={api} />
        </div>
        <div className="relative mt-4 max-w-xl">
          <Search size={14} className="absolute left-3 top-3 text-[#8A8A82]" />
          <Input data-testid="vcb-stage-customer-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search customers by name or e-mail…" className="pl-9 bg-black/40 border-white/15" />
        </div>
        {q && (
          <div className="mt-2 max-w-xl border border-[#D8B23E]/30 bg-[#0A0906] divide-y divide-white/5" data-testid="vcb-stage-customer-results">
            {list.map((c) => (
              <button key={c.id} data-testid={`vcb-stage-pick-${c.id}`} onClick={() => { setPicked(c); setQ(""); }} className="w-full text-left px-4 py-2.5 hover:bg-[#D8B23E]/10 flex justify-between text-[12px]">
                <span className="text-[#F5F5F0]">{c.name || "—"} <span className="text-[#8A8A82]">· {c.email}</span></span>
                <span className="font-grotesk text-[9px] uppercase tracking-[0.2em] text-[#8A8A82]">{c.vcb_status}</span>
              </button>
            ))}
            {list.length === 0 && <div className="px-4 py-3 text-[12px] text-[#8A8A82]">No matches</div>}
          </div>
        )}
      </div>
      <RMInbox api={api} onPick={(t) => setPicked({ id: t.user_id, email: t.email, name: t.name })} activeId={picked?.id} refreshKey={inboxTick} />
      {picked && <ReleaseStagesPanel api={api} customer={picked} key={picked.id} autoRelease={picked.autoRelease} onReleased={() => { loadReport(); loadCustomers(); }} />}
      {report && <StageReport api={api} report={report} overdueDays={overdueDays ?? report.overdue_days} setOverdueDays={(v) => { setOverdueDays(v); saveThreshold(v); }} onDigest={loadReport} onPick={(c) => setPicked({ id: c.id, email: c.email, name: c.name, autoRelease: c.next_stage })} />}
    </div>
  );
}

function StageReport({ api, report, overdueDays, setOverdueDays, onPick, onDigest }) {
  const [sending, setSending] = useState(false);
  const digest = async () => {
    setSending(true);
    try {
      const r = await api.post("/admin/vcb/stages-digest/run-now");
      toast.success(r.data.overdue_count ? `Digest sent to ${r.data.recipients.length} admin inbox${r.data.recipients.length === 1 ? "" : "es"} · ${r.data.overdue_count} overdue` : "No customers overdue — nothing to send");
      onDigest?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Digest failed"); } finally { setSending(false); }
  };
  return (
    <section className="border border-white/10 bg-black/40 p-6" data-testid="vcb-stage-report">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="font-grotesk text-[9px] uppercase tracking-[0.34em] text-[#8A6A1A]">VCB Stage Release report</div>
          <h2 className="font-serif text-2xl text-[#F5F5F0] mt-1">Where every customer stands</h2>
        </div>
        <div className="flex flex-wrap items-center gap-4">
          <label className="text-[11px] font-body text-[#8A8A82] inline-flex items-center gap-2">Overdue after
            <Input data-testid="vcb-overdue-threshold" type="number" min={1} max={365} value={overdueDays} onChange={(e) => setOverdueDays(e.target.value)} className="w-20 h-8 bg-black/40 border-white/15 text-xs" /> days
          </label>
          <button data-testid="vcb-send-digest" onClick={digest} disabled={sending} className="inline-flex items-center gap-2 border border-[#D8B23E]/60 text-[#D8B23E] px-4 py-2 font-grotesk text-[9px] uppercase tracking-[0.26em] hover:bg-[#D8B23E] hover:text-black disabled:opacity-50">
            {sending ? <Loader2 size={11} className="animate-spin" /> : <Mail size={11} />} Send digest now
          </button>
        </div>
      </div>
      <div className="font-body text-[11px] text-[#8A8A82] mt-2" data-testid="vcb-digest-status">
        Weekly digest · every Monday 09:00 IST to all admins · {report.last_digest ? <>last sent {fmt(report.last_digest.run_at)} · {report.last_digest.overdue_count} overdue · {report.last_digest.recipients?.length || 0} recipients</> : "not sent yet"}
      </div>
      <div className="grid grid-cols-2 md:grid-cols-6 gap-3 mt-6" data-testid="vcb-stage-counts">
        <div className="border border-white/10 p-3"><div className="font-grotesk text-[8px] uppercase tracking-[0.26em] text-[#8A8A82]">Not started</div><div className="font-serif text-2xl text-[#C9C9C2] mt-1">{report.not_started}</div></div>
        {report.per_stage.map((p) => (
          <div key={p.stage} className="border border-[#D8B23E]/25 p-3" data-testid={`vcb-stage-count-${p.stage}`}>
            <div className="font-grotesk text-[8px] uppercase tracking-[0.26em] text-[#8A6A1A]">Stage {p.numeral}</div>
            <div className="font-serif text-2xl text-[#D8B23E] mt-1">{p.customers}</div>
            <div className="text-[10px] text-[#8A8A82] truncate">{p.title}</div>
          </div>
        ))}
      </div>
      <div className="grid md:grid-cols-2 gap-6 mt-6">
        <div>
          <div className="font-grotesk text-[9px] uppercase tracking-[0.28em] text-[#D8B23E] mb-2">Cadence</div>
          <div className="font-body text-[13px] text-[#C9C9C2]" data-testid="vcb-avg-days">Average days between stage releases: <b className="text-[#F5F5F0]">{report.avg_days_between_stages ?? "—"}</b></div>
          <div className="mt-2 flex flex-wrap gap-2 text-[11px] text-[#8A8A82]">
            {Object.entries(report.avg_days_to_stage || {}).map(([k, v]) => <span key={k} className="border border-white/10 px-2 py-1">→ Stage {ROMAN[k]}: {v}d</span>)}
          </div>
          <div className="font-grotesk text-[9px] uppercase tracking-[0.28em] text-[#D8B23E] mt-6 mb-2 inline-flex items-center gap-2"><AlertTriangle size={11} /> Overdue for next stage ({report.overdue.length})</div>
          <div className="divide-y divide-white/5 border border-white/10" data-testid="vcb-overdue-list">
            {report.overdue.length === 0 && <div className="px-3 py-3 text-[12px] text-[#8A8A82]">No customers overdue.</div>}
            {report.overdue.map((c) => (
              <button key={c.id} onClick={() => onPick(c)} data-testid={`vcb-overdue-${c.id}`} className="w-full text-left px-3 py-2.5 hover:bg-[#D8B23E]/10 flex justify-between text-[12px]">
                <span className="text-[#F5F5F0]">{c.name || c.email}</span>
                <span className="text-[#8A8A82]">Stage {ROMAN[c.current_stage]} for <b className="text-[#E08A8A]">{c.days_at_stage}d</b> · <span className="text-[#D8B23E]">Release Stage {ROMAN[c.next_stage]}</span></span>
              </button>
            ))}
          </div>
        </div>
        <div>
          <div className="font-grotesk text-[9px] uppercase tracking-[0.28em] text-[#D8B23E] mb-2 inline-flex items-center gap-2"><History size={11} /> Release history</div>
          <div className="divide-y divide-white/5 border border-white/10 max-h-[380px] overflow-y-auto" data-testid="vcb-release-history">
            {report.history.length === 0 && <div className="px-3 py-3 text-[12px] text-[#8A8A82]">No releases yet.</div>}
            {report.history.map((h, i) => (
              <div key={i} className="px-3 py-2.5 text-[12px] flex flex-wrap justify-between gap-2">
                <span className="text-[#F5F5F0]">{h.name || h.email} <span className="text-[#D8B23E]">· Stage {ROMAN[h.stage]} {h.title}</span></span>
                <span className="text-[#8A8A82]">{fmt(h.released_at)} · {h.released_by}</span>
                {h.note && <span className="basis-full font-serif italic text-[11px] text-[#A9A082]">“{h.note}”</span>}
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function InvitationPreview({ api }) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [doc, setDoc] = useState(null);
  const load = useCallback((n) => api.get(`/admin/vcb/invitation-preview?name=${encodeURIComponent(n || "")}`).then((r) => setDoc(r.data)).catch(() => toast.error("Preview unavailable")), [api]);
  useEffect(() => { if (open) load(name); }, [open, load]); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <>
      <button type="button" onClick={() => setOpen(true)} data-testid="vcb-invite-preview-btn"
              className="inline-flex items-center gap-2 border border-[#D8B23E]/60 text-[#D8B23E] px-4 py-2 font-grotesk text-[9px] uppercase tracking-[0.26em] hover:bg-[#D8B23E] hover:text-black">
        <Eye size={11} /> Preview invitation e-mail
      </button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#0A0906] border-[#D8B23E]/40 text-[#F5F5F0] max-w-3xl" data-testid="vcb-invite-preview">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl text-[#D8B23E]">VCB Invitation e-mail</DialogTitle>
            <DialogDescription className="font-body text-[12px] text-[#A9A9A2]">Sent automatically — once, before Stage I — the moment a customer is added to Vinci Core Banking. Subject: <span className="text-[#F5F5F0]">{doc?.subject}</span></DialogDescription>
          </DialogHeader>
          <div className="flex gap-2 items-center">
            <Input data-testid="vcb-invite-preview-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Customer name for the preview…" className="h-9 bg-black/40 border-white/15 text-sm max-w-xs" />
            <Button onClick={() => load(name)} className="bg-[#D8B23E] text-black font-grotesk uppercase tracking-[0.22em] text-[9px] h-9 hover:bg-[#E5C450]" data-testid="vcb-invite-preview-refresh">Refresh</Button>
          </div>
          <div className="border border-white/10 bg-[#050506]">
            {doc ? <iframe title="Invitation preview" data-testid="vcb-invite-preview-frame" srcDoc={doc.html} sandbox="" className="w-full h-[62vh] bg-[#050506]" /> : <div className="h-[62vh] grid place-items-center"><Loader2 className="animate-spin text-[#D8B23E]" /></div>}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

function RMInbox({ api, onPick, activeId, refreshKey }) {
  const [threads, setThreads] = useState(null);
  const load = useCallback(() => api.get("/admin/vcb/rm/threads").then((r) => setThreads(r.data.threads)).catch(() => {}), [api]);
  useEffect(() => { load(); const t = setInterval(load, 30000); return () => clearInterval(t); }, [load, refreshKey]);
  if (!threads?.length) return null;
  const unread = threads.reduce((a, t) => a + t.unread, 0);
  return (
    <section className="border border-white/10 bg-black/40 p-6" data-testid="vcb-rm-inbox">
      <div className="font-grotesk text-[9px] uppercase tracking-[0.34em] text-[#8A6A1A] inline-flex items-center gap-2"><MessageSquare size={11} /> Relationship manager desk {unread > 0 && <span data-testid="vcb-rm-inbox-unread" className="rounded-full bg-[#D8B23E] text-black text-[9px] font-bold px-1.5 py-0.5">{unread}</span>}</div>
      <div className="mt-3 divide-y divide-white/5 border border-white/10">
        {threads.map((t) => (
          <button key={t.user_id} type="button" onClick={() => onPick(t)} data-testid={`vcb-rm-thread-${t.user_id}`}
                  className={`w-full text-left px-4 py-3 flex flex-wrap justify-between gap-2 hover:bg-[#D8B23E]/10 ${activeId === t.user_id ? "bg-[#D8B23E]/5" : ""}`}>
            <span className="text-[12px] text-[#F5F5F0]">{t.name || t.email} <span className="text-[#8A8A82]">· {t.email}</span>{t.rm && <span className="text-[#8A8A82]"> · RM {t.rm}</span>}
              <div className={`font-serif text-[12px] mt-0.5 ${t.unread ? "text-[#F3EEE2]" : "text-[#8A8A82]"}`}>{t.last_from === "desk" ? "You: " : ""}{t.last}</div></span>
            <span className="text-[11px] text-[#8A8A82] inline-flex items-center gap-2">{fmt(t.last_at)} {t.unread > 0 && <span className="rounded-full bg-[#D8B23E] text-black text-[9px] font-bold px-1.5 py-0.5">{t.unread}</span>}</span>
          </button>
        ))}
      </div>
    </section>
  );
}

function RMThreadAdmin({ api, customer, rm }) {
  const [msgs, setMsgs] = useState(null);
  const [body, setBody] = useState("");
  const [busy, setBusy] = useState(false);
  const endRef = useRef(null);
  const load = useCallback(() => api.get(`/admin/vcb/rm/threads/${customer.id}`).then((r) => { setMsgs(r.data.messages); window.dispatchEvent(new Event("vcb-rm-thread-changed")); }).catch(() => {}), [api, customer.id]);
  useEffect(() => { load(); const t = setInterval(load, 20000); return () => clearInterval(t); }, [load]);
  useEffect(() => { endRef.current?.scrollIntoView({ block: "nearest" }); }, [msgs]);
  const send = async () => {
    if (!body.trim()) return;
    setBusy(true);
    try { const r = await api.post(`/admin/vcb/rm/threads/${customer.id}/reply`, { body: body.trim() }); setMsgs((m) => [...(m || []), r.data.message]); setBody(""); toast.success("Reply sent · customer notified"); window.dispatchEvent(new Event("vcb-rm-thread-changed")); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not send"); } finally { setBusy(false); }
  };
  return (
    <div className="mt-6 border border-white/10 bg-black/40 p-6" data-testid="vcb-admin-rm-thread">
      <div className="font-grotesk text-[9px] uppercase tracking-[0.34em] text-[#D8B23E] inline-flex items-center gap-2"><MessageSquare size={11} /> Thread with {customer.name || customer.email} · replies go out as {rm.name}</div>
      <div className="mt-4 max-h-80 overflow-y-auto space-y-3 pr-1">
        {msgs?.length === 0 && <div className="font-body text-[12px] text-[#8A8A82]">No messages yet. Anything you write here reaches the customer in their Vinci Core sidebar and by e-mail.</div>}
        {msgs?.map((m) => (
          <div key={m.id} data-testid={`vcb-admin-rm-msg-${m.from}`} className={`max-w-[85%] ${m.from === "desk" ? "ml-auto" : ""}`}>
            <div className={`px-3 py-2 text-[13px] leading-relaxed whitespace-pre-wrap ${m.from === "desk" ? "bg-[#1A160A] text-[#F3EEE2] border border-[#D8B23E]/30" : "bg-[#0F0E0B] text-[#E8DFC4] border border-white/10"}`}>{m.body}</div>
            <div className={`font-body text-[9px] text-[#6A6A64] mt-1 ${m.from === "desk" ? "text-right" : ""}`}>{m.author} · {fmt(m.created_at)}</div>
          </div>
        ))}
        <div ref={endRef} />
      </div>
      <div className="mt-3 flex gap-2">
        <Textarea data-testid="vcb-admin-rm-input" value={body} onChange={(e) => setBody(e.target.value)} rows={2} maxLength={2000} placeholder={`Reply as ${rm.name}…`} className="bg-black/40 border-white/15 text-sm resize-none" />
        <Button onClick={send} disabled={busy || !body.trim()} data-testid="vcb-admin-rm-send" className="self-end bg-[#D8B23E] text-black font-grotesk uppercase tracking-[0.24em] text-[10px] hover:bg-[#E5C450]">{busy ? <Loader2 size={12} className="animate-spin" /> : "Send"}</Button>
      </div>
    </div>
  );
}
